// Libraries
import { useSelector } from 'react-redux'

/**
 * @typedef {Object} UseSettingsOptions
 *
 * @property {string} [default] - Default value to return if the setting is not found
 * @property {Function} [transformer] - Optional function to transform the value before returning
 */

/**
 * Custom hook to retrieve settings from Redux store using Immutable.js Map.
 * Returns a fallback value if the setting is not found or is null/undefined.
 *
 * @param {string} key - The key to lookup in the settings Map
 * @param {UseSettingsOptions} [options] - Optional options object
 * @returns {*} The value from settings (transformed if specified) or fallback if not found
 *
 * @example
 * @example
 * // Get a setting
 * const useBusinessHours = useSettings('store.use_business_hours')
 *
 * // Get a setting with default value
 * const useBusinessHours = useSettings('store.use_business_hours', {
 *   default: true
 * })
 *
 * @example
 * // Get a setting with transformation
 * const timeIncrement = useSettings('store.time_increment', {
 *   transformer: (value) => parseInt(value)
 * })
 */
export const useSettings = (key, options = {}) => {
  const value = useSelector((state) => state.settings.get(key))

  if (options.default) {
    return value == null ? options.default : value
  }

  return options.transformer ? options.transformer(value) : value
}
